import type { Project } from "@/api/spaces";
import { AppUtilityControls } from "@/components/panels/app-utility-controls";
import { RailProjectSwitcher } from "@/components/layout/rail-project-switcher";
import { utilityButtonClass } from "@/components/panels/app-utility-control-styles";
import { Button, Icons } from "@/components/ui";
import { TrayTooltip } from "@/desktop/tray-panel-controls";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { layoutMessages } from "@/i18n/messages/layout";

interface DesktopRailUtilitiesProps {
  onSearch: () => void;
  onOpenNotifications: () => void;
  onCloseWorkspace?: () => void;
  /** Mirrors the rail's visibility — closes popovers when the rail hides. */
  railVisible?: boolean;
  /** Opens project management; a newly created project opens its details. */
  onManageProjects?: (project?: Project) => void;
}

export function DesktopRailUtilities({
  onSearch,
  onOpenNotifications,
  onCloseWorkspace,
  railVisible,
  onManageProjects,
}: DesktopRailUtilitiesProps) {
  const t = useT(layoutMessages);
  return (
    <div className="flex flex-col items-center gap-1">
      {onManageProjects ? (
        <RailProjectSwitcher onManageProjects={onManageProjects} />
      ) : null}
      {onCloseWorkspace ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(utilityButtonClass, "group relative")}
          onClick={onCloseWorkspace}
          data-testid="rail-close-workspace"
          aria-label={t("closeWorkspace")}
          aria-describedby="rail-close-workspace-tooltip"
        >
          <Icons.Frame className="h-4 w-4" aria-hidden="true" />
          <TrayTooltip
            id="rail-close-workspace-tooltip"
            label={t("closeWorkspace")}
            shortcut="shortcut-toggle-workspace"
            placement="right"
          />
        </Button>
      ) : null}
      <AppUtilityControls
        orientation="vertical"
        railVisible={railVisible}
        onSearch={onSearch}
        showUserMenu={false}
        onOpenNotifications={onOpenNotifications}
        testIdPrefix="rail"
      />
    </div>
  );
}

export type { DesktopRailUtilitiesProps };
